import { Action, Button } from '@op/opds.opux';
import { Modal } from '@op/opds.opux-modal';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import DiscountPercentage from './DiscountPercentage';
import { AnalyticsPhases, trackAction } from '../../../api/analytics-api';
import { APP_ELEMENT_ID } from '../../../constants';
import { getInitialState } from '../../../utils';
import { type Discount, DiscountType } from 'dto/CoveragePremium';

export type CustomerType =
  | 'owner'
  | 'prime'
  | 'unAuthenticated'
  | 'simulatedPrime';

interface DiscountActionProps {
  discount: Discount;
  discountType: DiscountType;
  customerType?: CustomerType;
  textKey: string;
}

interface DiscountTextProps {
  discountType: DiscountType;
  textKey: string;
  onActionClick: () => void;
  discountPercentage?: number;
}

const analyticsEventNames: Record<DiscountType, string> = {
  [DiscountType.DISCRETIONARY_DISCOUNT]: 'henkivakuutus lisaetu',
  [DiscountType.WAIT_PERIOD_DISCOUNT]: 'vakavansairaudenturva karenssivahennys',
};

const DiscountText = ({
  discountType,
  textKey,
  onActionClick,
  discountPercentage,
}: DiscountTextProps) => {
  if (
    discountType !== DiscountType.DISCRETIONARY_DISCOUNT &&
    discountType !== DiscountType.WAIT_PERIOD_DISCOUNT
  ) {
    return null;
  }

  return (
    <p data-testid={`discount-text-${discountType}`}>
      <Trans
        i18nKey={textKey}
        components={{
          action: (
            <Action
              inline
              onClick={onActionClick}
              data-testid={`discount-action-${discountType}`}
            />
          ),
          ...(discountType === DiscountType.DISCRETIONARY_DISCOUNT && {
            discount: <DiscountPercentage value={discountPercentage} />,
          }),
        }}
      />
    </p>
  );
};

const DiscountAction = ({
  discount,
  discountType,
  customerType,
  textKey,
}: DiscountActionProps) => {
  const { authenticatedCustomer } = getInitialState();
  const { t } = useTranslation(['individualLife']);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const closedByFooterButton = useRef(false);
  const hasBeenOpened = useRef(false);

  const discountTypeName = t(`discount.${discountType}`);
  const percentage = discount.percentage ? ` -${discount.percentage}%` : '';
  const modalTitle = `${discountTypeName}${percentage}`;

  const handleModalOpen = () => {
    setIsModalOpen(true);
    hasBeenOpened.current = true;
    trackAction(
      `${analyticsEventNames[discountType]} opened`,
      'modal',
      authenticatedCustomer
        ? AnalyticsPhases.BENEFICIARIES_AND_DOCUMENTS
        : AnalyticsPhases.PRICE
    );
  };

  const getModalContent = () => {
    if (discountType === DiscountType.DISCRETIONARY_DISCOUNT && customerType) {
      const infoKey = `priceSummary.discountInformation.DISCRETIONARY_DISCOUNT.${customerType}.info`;
      const additionalInfoKey = `priceSummary.discountInformation.DISCRETIONARY_DISCOUNT.${customerType}.additionalInfo`;
      return {
        info: t(infoKey, { percentage: discount.percentage }),
        additionalInfo: t(additionalInfoKey),
      };
    } else if (discountType === DiscountType.WAIT_PERIOD_DISCOUNT) {
      const infoKey = `priceSummary.discountInformation.WAIT_PERIOD_DISCOUNT.info`;
      const additionalInfoKey = `priceSummary.discountInformation.WAIT_PERIOD_DISCOUNT.additionalInfo`;
      return {
        info: t(infoKey),
        additionalInfo: t(additionalInfoKey),
      };
    }
    return { info: '', additionalInfo: '' };
  };

  const modalContent = getModalContent();

  return (
    <>
      <DiscountText
        discountType={discountType}
        textKey={textKey}
        onActionClick={handleModalOpen}
        discountPercentage={discount.percentage}
      />
      <Modal
        appElement={document.getElementById(APP_ELEMENT_ID) ?? undefined}
        showCloseButton={true}
        isOpen={isModalOpen}
        title={modalTitle}
        titleProps={{
          'data-testid': `discount-modal-title-${discountType}`,
        }}
        onUpdate={({ open }: { open: boolean }) => {
          if (!open && hasBeenOpened.current) {
            const buttonName = closedByFooterButton.current ? 'SULJE' : 'X';
            trackAction(
              `${analyticsEventNames[discountType]} closed from ${buttonName} button`,
              'modal',
              authenticatedCustomer
                ? AnalyticsPhases.BENEFICIARIES_AND_DOCUMENTS
                : AnalyticsPhases.PRICE
            );
            closedByFooterButton.current = false;
          }
          setIsModalOpen(open);
        }}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}>
            <Button
              compact
              onClick={() => {
                closedByFooterButton.current = true;
                setIsModalOpen(false);
              }}
              data-testid={`discount-modal-close-${discountType}`}>
              {t('coverageProduct.closeModal')}
            </Button>
          </div>
        }>
        <div className='opux-v6__opds'>
          {modalContent.info && (
            <p data-testid={`discount-modal-info-${discountType}`}>
              {modalContent.info}
            </p>
          )}
          {modalContent.additionalInfo && (
            <p data-testid={`discount-modal-additional-info-${discountType}`}>
              {modalContent.additionalInfo}
            </p>
          )}
        </div>
      </Modal>
    </>
  );
};

export default DiscountAction;
